import React, { useEffect, useMemo, useState } from 'react';
import { Table, Typography, Space, Button, Tag, Spin, Tooltip, Input, Card, Row, Col, Select } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { SearchOutlined } from '@ant-design/icons';
import { trpc } from '../lib/api';
import { DocumentLedgerCreateModal } from '../components/document/DocumentLedgerCreateModal';
import { CategoryTag } from '../components/common/CategoryTag';
import { CategoryTagCloudFilter } from '../components/common/CategoryTagCloudFilter';
import {
  DEFAULT_DOCUMENT_LIST_SORT,
  DOCUMENT_LIST_SORT_STORAGE_KEY,
  getSortOrderLabel,
  loadSortPreference,
  persistSortPreference,
  type DocumentListSortField,
  type SortPreference,
} from './utils/sortPreferences';

const { Title, Text } = Typography;

type DocumentTypeOption = {
  id: string;
  name: string;
  color?: string | null;
  icon?: string | null;
  isEnabled?: boolean;
};

type DocumentAssociationType = 'location' | 'equipment' | 'part' | 'supplier' | 'user' | 'purchase_order';

type DocumentListRow = {
  document: {
    id: string;
    title: string;
    category: string;
    createdAt?: string | null;
    documentTypeId?: string | null;
  };
  documentType?: DocumentTypeOption | null;
  association?: {
    entityType: DocumentAssociationType;
  } | null;
  associationLocation?: { name?: string | null } | null;
  associationEquipment?: { name?: string | null; displayId?: string | null } | null;
  associationPart?: { name?: string | null; displayId?: string | null } | null;
  associationSupplier?: { name?: string | null } | null;
  associationUser?: { name?: string | null; email?: string | null } | null;
  createdByUser?: { name?: string | null; email?: string | null } | null;
};

const entityTypeLabels: Record<string, string> = {
  location: 'エリア',
  equipment: '設備',
  part: '部品',
  supplier: '業者',
  user: 'ユーザー',
  purchase_order: '発注',
};

const formatDate = (value?: string | null) => {
  if (!value) return '-';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '-' : date.toLocaleDateString('ja-JP');
};

const formatUser = (user?: { name?: string | null; email?: string | null } | null) => {
  if (!user) return '-';
  return user.name || user.email || '-';
};

const resolveAssociationName = (record: DocumentListRow) => {
  const association = record.association;
  if (!association) return null;
  switch (association.entityType) {
    case 'location':
      return record.associationLocation?.name ?? null;
    case 'equipment': {
      const name = record.associationEquipment?.name ?? null;
      const displayId = record.associationEquipment?.displayId ?? null;
      return name && displayId ? `${name} (${displayId})` : name;
    }
    case 'part': {
      const name = record.associationPart?.name ?? null;
      const displayId = record.associationPart?.displayId ?? null;
      return name && displayId ? `${name} (${displayId})` : name;
    }
    case 'supplier':
      return record.associationSupplier?.name ?? null;
    case 'user':
      return record.associationUser?.name ?? record.associationUser?.email ?? null;
    default:
      return null;
  }
};

const resolveAssociationDisplay = (record: DocumentListRow) => {
  const association = record.association;
  if (!association) {
    return '-';
  }
  const typeLabel = entityTypeLabels[association.entityType] ?? association.entityType;
  const name = resolveAssociationName(record);
  return name ? `${typeLabel} / ${name}` : typeLabel;
};

export const DocumentsPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [createOpen, setCreateOpen] = useState(false);
  const [searchText, setSearchText] = useState('');
  const [selectedCategory, setSelectedCategory] = useState<string | undefined>(
    searchParams.get('category') || undefined
  );
  const [sortPreference, setSortPreference] = useState<SortPreference<DocumentListSortField>>(() =>
    loadSortPreference(
      DOCUMENT_LIST_SORT_STORAGE_KEY,
      ['title', 'createdAt', 'association'] as const,
      DEFAULT_DOCUMENT_LIST_SORT,
    )
  );
  const { data: profile } = trpc.user.getProfile.useQuery();
  const canCreate = profile?.role ? profile.role !== 'viewer' : false;
  const { data: documentTypes = [], isLoading: isTypesLoading } = trpc.document.listTypes.useQuery();
  const enabledDocumentTypes = useMemo(
    () => (documentTypes as DocumentTypeOption[]).filter((type) => type.isEnabled !== false),
    [documentTypes]
  );
  const { data, isLoading } = trpc.document.list.useQuery({
    page: 1,
    limit: 50,
    search: searchText.trim() || undefined,
    documentTypeId: selectedCategory && selectedCategory.trim() !== '' ? selectedCategory : undefined,
  });

  const rows = useMemo(
    () => (data?.items ?? []) as DocumentListRow[],
    [data?.items]
  );
  const sortedRows = useMemo(() => {
    const collator = new Intl.Collator('ja');
    const sorted = [...rows];

    sorted.sort((left, right) => {
      let comparison = 0;

      if (sortPreference.field === 'title') {
        comparison = collator.compare(left.document.title, right.document.title);
      } else if (sortPreference.field === 'association') {
        comparison = collator.compare(resolveAssociationDisplay(left), resolveAssociationDisplay(right));
      } else {
        const leftTime = left.document.createdAt ? new Date(left.document.createdAt).getTime() : 0;
        const rightTime = right.document.createdAt ? new Date(right.document.createdAt).getTime() : 0;
        comparison = leftTime - rightTime;
      }

      if (comparison === 0) {
        comparison = collator.compare(left.document.title, right.document.title);
      }

      return sortPreference.order === 'asc' ? comparison : -comparison;
    });

    return sorted;
  }, [rows, sortPreference]);
  const documentTypeCounts = useMemo(() => {
    const counts: Record<string, number> = {};
    rows.forEach((row) => {
      const typeId = row.document?.documentTypeId;
      if (!typeId) return;
      counts[typeId] = (counts[typeId] ?? 0) + 1;
    });
    return counts;
  }, [rows]);

  useEffect(() => {
    persistSortPreference(DOCUMENT_LIST_SORT_STORAGE_KEY, sortPreference);
  }, [sortPreference]);

  const columns: ColumnsType<DocumentListRow> = [
    {
      title: '書類名',
      dataIndex: ['document', 'title'],
      key: 'title',
      render: (value: string, record) => (
        <Button
          type="link"
          onClick={() => navigate(`/app/documents/${record.document.id}`)}
          style={{ padding: 0 }}
        >
          <Text strong>{value}</Text>
        </Button>
      ),
    },
    {
      title: '書類カテゴリ',
      key: 'type',
      render: (_: unknown, record) => (
        <CategoryTag
          category={record.documentType ?? null}
          linkPath="/app/documents"
        />
      ),
    },
    {
      title: '区分',
      dataIndex: ['document', 'category'],
      key: 'category',
      render: (value: string) => (
        <Tag color={value === 'official' ? 'red' : 'default'}>
          {value === 'official' ? '法定点検' : '一般'}
        </Tag>
      ),
    },
    {
      title: '関連',
      key: 'association',
      render: (_: unknown, record) => resolveAssociationDisplay(record),
    },
    {
      title: '作成者',
      key: 'createdBy',
      render: (_: unknown, record) => formatUser(record.createdByUser),
    },
    {
      title: '作成日',
      dataIndex: ['document', 'createdAt'],
      key: 'createdAt',
      render: (value?: string | null) => formatDate(value),
    },
  ];

  return (
    <div>
      <Space direction="vertical" style={{ width: '100%' }} size="large">
        <Space style={{ width: '100%', justifyContent: 'space-between' }} align="center">
          <Title level={3} style={{ margin: 0 }}>書類</Title>
          <Space>
            <Tooltip title={canCreate ? undefined : '閲覧権限では作成できません'}>
              <Button type="primary" onClick={() => setCreateOpen(true)} disabled={!canCreate}>
                書類登録
              </Button>
            </Tooltip>
            <Button type="default" onClick={() => navigate('/app/categories#document')}>
              カテゴリ管理
            </Button>
            <Button type="default" onClick={() => navigate('/app/documents/official-inspections')}>
              法定点検ビュー
            </Button>
          </Space>
        </Space>

        <Card style={{ marginBottom: 8 }}>
          <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 12 }}>
            <Col xs={24} sm={12} md={10}>
              <Input
                placeholder="書類名、説明、ファイル名、変更メモで検索"
                prefix={<SearchOutlined />}
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
                allowClear
              />
            </Col>
            <Col xs={24} sm={12} md={8}>
              <Select
                style={{ width: '100%' }}
                value={sortPreference.field}
                onChange={(value) =>
                  setSortPreference((current) => ({
                    ...current,
                    field: value as DocumentListSortField,
                  }))
                }
                data-testid="documents-sort-field-select"
              >
                <Select.Option value="createdAt">作成日</Select.Option>
                <Select.Option value="title">書類名</Select.Option>
                <Select.Option value="association">関連先</Select.Option>
              </Select>
            </Col>
            <Col xs={24} sm={12} md={6}>
              <Select
                style={{ width: '100%' }}
                value={sortPreference.order}
                onChange={(value) =>
                  setSortPreference((current) => ({
                    ...current,
                    order: value as SortPreference<DocumentListSortField>['order'],
                  }))
                }
                data-testid="documents-sort-order-select"
              >
                <Select.Option value="asc">{getSortOrderLabel(sortPreference.field, 'asc')}</Select.Option>
                <Select.Option value="desc">{getSortOrderLabel(sortPreference.field, 'desc')}</Select.Option>
              </Select>
            </Col>
          </Row>
          <CategoryTagCloudFilter
            categories={enabledDocumentTypes.map((type) => ({
              id: type.id,
              categoryLabel: type.name,
              color: type.color,
              icon: type.icon,
              count: documentTypeCounts[type.id] ?? 0,
            }))}
            selectedCategory={selectedCategory}
            onCategoryChange={(categoryId) => setSelectedCategory(categoryId)}
            loading={isTypesLoading}
            showCounts={true}
            maxVisibleTags={15}
          />
        </Card>

        {isLoading ? (
          <div style={{ textAlign: 'center', padding: 24 }}>
            <Spin />
          </div>
        ) : (
          <Table
            rowKey={(record) => record.document.id}
            dataSource={sortedRows}
            columns={columns}
            pagination={false}
            scroll={{ x: 'max-content' }}
          />
        )}
      </Space>

      <DocumentLedgerCreateModal
        open={createOpen}
        onCancel={() => setCreateOpen(false)}
        onSuccess={() => setCreateOpen(false)}
      />
    </div>
  );
};

export default DocumentsPage;
